import React, { Component } from 'react';
import * as moment from 'moment';
import Swal from 'sweetalert2';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import { withTranslation } from 'react-i18next';

import {
	GetFormat,
	GetUtcDate,
	setNavigationalWindowObject,
	deleteNavigationalWindowObject,
	convertToLangKey
} from '../../../AppUtil/AppUtil';
import Session from '../../../Service/Session';
import LoanService from '../../../Service/LoanService';
import InvitationService from '../../../Service/InvitationService';
import AmortizationTableComponent from '../../AmortizationTableComponent';
import DwollaPlaid from '../../Common/DwollaPlaid';
import {
	toastWhenPaymentMethodCashPastDate,
	toastWhenPaymentMethodBank,
	toastWhenViewSummaryRender,
	popUpForItemDescribed
} from '../../../SweetAlertUtil/util';
import TermsAndConditions from '../../Profile/TermsAndConditions';
import { logPageView } from '../../../AppUtil/analytics';
import Base from '../../Common/LoanViewComponent';
import Fields from '../../Common/LoanViewComponent/Fields';
import Columns from '../../Common/LoanViewComponent/Column';
import ButtonColumns from '../../Common/LoanViewComponent/ButtonColumns';
import Buttons from './Buttons';
import PaymentMethod from '../../Common/PaymentMethod';
import { hot } from 'react-hot-loader';

let emiApiResponse = [];

const API_URLS = window.AppConfig.API_URLS;
const LOAN_OBJECT = 'LOAN_OBJECT';
const LOAN_TYPES = window.AppConfig.LOAN_TYPES;

class ViewSummary extends Component {
	constructor(props) {
		super(props);
		this.state = {
			_loanObjectId: null,
			_isInviteSending: false,
			_openScreen: 'DETAILS',
			_isTableLoaded: false,
			_loanObject: {},
			lenderState: '',
			_successMessage: '',
			_headingName: '',
			_checkTypeForDwollaScreen: '',
			_fromAddBankAccount: false,
			_isLoanCreated: false,
			_headingEmail: '',
			_headingPhone: ''
		};
	}

	setDefaultHeadingForName = () => {
		const loanForm = Session.get(LOAN_OBJECT);
		if (loanForm.loan_type === 'LEND') {
			this.setState({
				_headingName: 'label.borrower',
				_headingEmail: 'label.borrower_email',
				_headingPhone: 'label.borrower_phone',
				_checkTypeForDwollaScreen: 'LEND'
			});
		} else {
			this.setState({
				_headingName: 'label.lender',
				_headingEmail: 'label.lender_email',
				_headingPhone: 'label.lender_phone',
				_checkTypeForDwollaScreen: 'BORROW'
			});
		}
	};

	openAmortizationTable = () => {
		this.setState({
			_openScreen: 'AMORTIZATION_TABLE'
		});
	};

	openPaymentMethodScreen = () => {
		this.setState({
			_openScreen: 'PAYMENT_METHOD'
		});
	};

	closePaymentMethodScreen = () => {
		this.setState({
			_openScreen: 'DETAILS'
		});
	};

	fetchAmortizationTable = async () => {
		this.onStartAmortizationFetching();
		try {
			await this.calculateEmi();
			this.onCloseAmortizationFetching();
		} catch (err) {
			console.log(err);
			this.onCloseAmortizationFetching();
		}
	};

	onCloseAmortizationFetching = () => {
		this.setState({
			_isTableLoaded: false
		});
	};

	onStartAmortizationFetching = () => {
		this.setState({
			_isTableLoaded: true
		});
	};

	onAmortizationClose = () => {
		// NOSONAR
		this.setState({
			_openScreen: 'DETAILS'
		});
	};

	onAgreeAndContinue = () => {
		const account = this.props.currentUser.profile.account || {};
		const loanForm = Session.get(LOAN_OBJECT);
		if (!account.customer_id || !account.funding_source_id || account.plaid_status === 'ITEM_LOGIN_REQUIRED') {
			this.redirectToDwollaPlaid();
			return false;
		}
		if (loanForm.loan_type === LOAN_TYPES.LEND && loanForm.workflow_id === 1) {
			this.openPaymentMethodScreen();
			return false;
		}
		this.sendInvite();
	};

	onCancel = () => {
		Session.remove('LOAN_OBJECT');
		Swal.fire({
			animation: false,
			allowOutsideClick: false,
			title: this.props.t('sweetalert_title.canceled'),
			text: this.props.t('sweetalert.loan_request_canceled'),
			icon: 'success',
			confirmButtonText: this.props.t('buttons.back_to_home'),
			customClass: 'otp_message_success',
			onClose: this.redirectToHome
		});
	};

	onBack = () => {
		deleteNavigationalWindowObject();
		const loanForm = Session.get(LOAN_OBJECT);
		this.props.onBack(loanForm.workflow_id);
	};

	createRequestBodyForLoan = () => {
		const loanForm = Session.get(LOAN_OBJECT);
		const requestBody = {
			start_date: GetUtcDate(new Date()),
			first_installment_date: GetUtcDate(new Date(loanForm.start_date)),
			no_of_installments: parseInt(loanForm.no_of_installments),
			annual_rate: parseFloat(loanForm.interest_rate),
			emi_amount: parseFloat(loanForm.emi_amount),
			frequency: loanForm.frequency,
			loan_type: loanForm.loan_type,
			principal: parseFloat(loanForm.principal),
			state: loanForm.state,
			reason: loanForm.reason ? loanForm.reason : '',
			collateral: loanForm.collateral,
			additional_terms: loanForm.additional_terms,
			lender_state: loanForm.lender_state,
			loan_workflow_id: this.props.idOfWorkflowSelected
		};
		if (loanForm.workflow_id === 2 || loanForm.workflow_id === 3) {
			requestBody.disbursal_date = loanForm.disbursal_date;
			requestBody.disbursal_mode = 'OFFLINE';
		}
		if (loanForm.workflow_id === 3) {
			requestBody.item_purchased = loanForm.item_purchased;
		}
		return requestBody;
	};

	createLoanObject = async () => {
		this.startLoader();
		try {
			const requestBody = this.createRequestBodyForLoan();
			const response = await LoanService.create(API_URLS.LOAN, requestBody);
			logPageView('/app/loan-created'); // NOSONAR
			this.setState(
				{
					_loanObjectId: response.data.id
				},
				() => {
					const loanObject = Session.get('LOAN_OBJECT');
					loanObject.loan_id = this.state._loanObjectId;
					Session.store('LOAN_OBJECT', loanObject);
					this.createInvite();
				}
			);
		} catch (err) {
			console.log(err);
			this.stopLoader();
		}
	};

	createInvite = async () => {
		try {
			const loanObject = Session.get('LOAN_OBJECT');
			const invitedUser = loanObject.other_info;
			const requestBody = {
				name: invitedUser.name,
				loan_id: this.state._loanObjectId
			};
			if (invitedUser.phone !== '') {
				requestBody.phone = invitedUser.countryCode + invitedUser.phone;
			}
			if (invitedUser.emailId !== '') {
				requestBody.email = invitedUser.emailId.toLowerCase();
			}
			if (invitedUser.relationship) {
				requestBody.relationship = invitedUser.relationship;
			} else {
				requestBody.relationship = '';
			}
			await InvitationService.create(requestBody);
			this.stopLoader();
			toastWhenViewSummaryRender();
		} catch (err) {
			console.log(err);
			this.stopSendingInviteLoader();
			this.toastSomethingWentWrong();
		}
	};

	updateDraftedLoanApiCall = async payment => {
		const requestBody = this.createRequestBodyForLoan();
		if (payment) {
			requestBody.disbursal_mode = payment.payment_mode;
			if (payment.payment_mode === 'OFFLINE') {
				requestBody.disbursal_date = GetUtcDate(payment.payment_date);
			}
		}
		const loanForm = Session.get(LOAN_OBJECT);
		await LoanService.updateDraftedLoan(loanForm.loan_id, requestBody);
	};

	updateCreatedLoan = async () => {
		try {
			this.startLoader();
			await this.updateDraftedLoanApiCall();
			this.stopLoader();
			toastWhenViewSummaryRender();
		} catch (error) {
			this.stopLoader();
			console.log(error);
		}
	};

	handlePaymentMethodCallback = payment => {
		if (payment) {
			if (payment.payment_mode === 'ACH') {
				toastWhenPaymentMethodBank(
					this.state._loanObject.other_info.name,
					payment,
					this.sendInvite,
					this.closePaymentMethodScreen,
					true
				);
			} else {
				if (moment(payment.payment_date).diff(moment(), 'day') < 0) {
					toastWhenPaymentMethodCashPastDate(payment, this.sendInvite, this.closePaymentMethodScreen);
				} else {
					this.sendInvite(payment);
					this.closePaymentMethodScreen();
				}
			}
		}
	};

	sendInvite = async payment => {
		try {
			this.startSendingInviteLoader();
			if (payment) {
				await this.updateDraftedLoanApiCall(payment);
			}
			const loanForm = Session.get(LOAN_OBJECT);
			const response = await InvitationService.resend(loanForm.loan_id);
			if (response.status) {
				this.invitationSent();
				logPageView('/app/loan-invite-sent');
			} else {
				this.toastSomethingWentWrong();
			}
			this.stopSendingInviteLoader();
		} catch (error) {
			this.stopLoader();
		}
	};

	calculateEmi = async () => {
		const loanForm = Session.get(LOAN_OBJECT);
		const requestBody = {
			principal: parseFloat(loanForm.principal),
			first_installment_date: GetUtcDate(loanForm.start_date),
			start_date: GetUtcDate(new Date()),
			annual_rate: parseFloat(loanForm.interest_rate),
			frequency: loanForm.frequency,
			no_of_installments: parseInt(loanForm.no_of_installments),
			loan_type: loanForm.loan_type
		};
		try {
			const response = await LoanService.create(API_URLS.GET_EMI_AMOUNT, requestBody);
			emiApiResponse = response.data;
			return emiApiResponse;
		} catch (error) {
			console.log(error);
			return null;
		}
	};

	startSendingInviteLoader = () => {
		this.setState({
			_isInviteSending: true
		});
	};

	stopSendingInviteLoader = () => {
		this.setState({
			_isInviteSending: false
		});
	};

	startLoader = () => {
		this.setState({
			_isLoanCreated: true
		});
	};

	stopLoader = () => {
		this.setState({
			_isLoanCreated: false
		});
	};

	invitationSent = () => {
		Swal.fire({
			animation: false,
			allowOutsideClick: false,
			title: this.props.t('sweetalert_title.sent'),
			text:
				this.props.t(this.state._successMessage) +
				' ' +
				this.state._loanObject.other_info.name +
				'. ' +
				this.props.t('sweetalert.additional_invitation_message'),
			icon: 'success',
			confirmButtonText: this.props.t('buttons.view_your_loan_list'),
			customClass: 'otp_message_success',
			onClose: this.redirectToLoanList
		});
	};

	toastWhenCancelLoan = () => {
		Swal.fire({
			allowOutsideClick: false,
			title: this.props.t('sweetalert_title.are_you_sure_you_want_to_cancel_this_loan'),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#3085d6',
			cancelButtonColor: '#d33',
			confirmButtonText: this.props.t('label.yes'),
			customClass: 'otp_message_success'
		}).then(result => {
			if (result.value) {
				this.onCancel();
			}
		});
	};

	toastSomethingWentWrong = () => {
		Swal.fire({
			allowOutsideClick: false,
			title: this.props.t('sweetalert_title.error'),
			text: this.props.t('error_message.something_went_wrong'),
			imageUrl: 'https://cdn-icons-png.flaticon.com/512/5219/5219070.png',
			imageAlt: 'Custom Image',
			// icon: 'error',
			confirmButtonText: this.props.t('buttons.try_again'),
			customClass: 'otp_message_success'
		});
	};

	addBankAccount = () => {
		const account = this.props.currentUser.profile.account || {};
		this.setState({
			_fromAddBankAccount: true
		});
		if (!account.customer_id || account.verification_status !== 'verified') {
			this.redirectToDwollaPlaid();
		} else if (!account.funding_source_id) {
			this.setState({
				_openScreen: 'TERMS_CONDITIONS'
			});
		}
	};

	redirectToHome = () => {
		this.props.openScreen('HOME');
	};

	redirectToLoanList = () => {
		deleteNavigationalWindowObject();
		this.props.openScreen('LOAN_LIST');
	};

	redirectToDwollaPlaid = () => {
		this.setState({
			_openScreen: 'DWOLLA_PLAID'
		});
	};

	backFromDwollaScreen = () => {
		// NOSONAR
		this.setState({
			_openScreen: 'DETAILS'
		});
	};

	closeDwollaPlaid = () => {
		this.setState({
			_openScreen: 'DETAILS'
		});
		if (!this.state._fromAddBankAccount) {
			this.onAgreeAndContinue();
		}
	};

	setMetaData = () => {
		const loanObject = Session.get('LOAN_OBJECT');
		const States = Session.get('STATES');
		const successMessage =
			loanObject.loan_type === 'LEND'
				? 'sweetalert.invite_sent_successfully_lend'
				: 'sweetalert.invite_sent_successfully_borrow';
		this.setState({
			_loanObject: loanObject,
			lenderState: States[loanObject.lender_state].name,
			_successMessage: successMessage
		});
	};

	handleFrequency = frequency => {
		if (frequency === 'TWO WEEK') {
			return 'Every Two Weeks';
		} else if (frequency === 'LUMP_SUM') {
			return 'LUMP SUM';
		} else {
			return frequency + 'LY';
		}
	};

	componentDidMount() {
		const loanObject = Session.get('LOAN_OBJECT');
		if (loanObject.loan_id) {
			this.updateCreatedLoan();
		} else {
			this.createLoanObject();
		}
		this.setDefaultHeadingForName();
		this.setMetaData();
		this.fetchAmortizationTable();
		logPageView('/app/review-loan-summary'); // NOSONAR
		window.addEventListener('beforeunload', this.listenEvent);
		const dataForPopUp = {
			title: 'sweetalert_title.wait',
			messageKey: 'sweetalert.continue_on_view_summary',
			message: {},
			label: ''
		};
		setNavigationalWindowObject(dataForPopUp);
	}

	listenEvent = e => {
		e.returnValue = '';
	};

	componentWillUnmount() {
		window.removeEventListener('beforeunload', this.listenEvent);
		deleteNavigationalWindowObject();
	}

	render() {
		// NOSONAR
		const account = this.props.currentUser.profile.account || {};
		const { t } = this.props;
		switch (this.state._openScreen) {
			case 'PAYMENT_METHOD':
				return (
					<PaymentMethod
						handleBack={this.closePaymentMethodScreen}
						handleContinue={this.handlePaymentMethodCallback}
						maxPaymentDate={this.state._loanObject.start_date}
					/>
				);
			case 'DWOLLA_PLAID':
				return (
					<DwollaPlaid
						currentUser={this.props.currentUser}
						onBack={this.backFromDwollaScreen}
						onClose={this.closeDwollaPlaid}
						openScreen={this.props.openScreen}
						setCurrentUser={this.props.setCurrentUser}
						supportedStates={this.props.supportedStates}
					/>
				);
			case 'AMORTIZATION_TABLE':
				return (
					<AmortizationTableComponent
						currentUser={this.props.currentUser}
						loanData={emiApiResponse}
						onAmortizationClose={this.onAmortizationClose}
						table={emiApiResponse.installments}
						typeOfUser={this.state._checkTypeForDwollaScreen}
					/>
				);
			case 'TERMS_CONDITIONS':
				return (
					<TermsAndConditions
						currentUser={this.props.currentUser.profile}
						handleBack={this.onAmortizationClose}
						handleContinue={this.redirectToDwollaPlaid}
					/>
				);
			default:
				return (
					<Base isLoading={this.state._isLoanCreated}>
						<Typography
							component="h1"
							style={{
								textAlign: 'center',
								marginLeft: '5%',
								marginBottom: '5%'
							}}
							variant="h4">
							{t('view_detail.loanlist_heading')}
						</Typography>
						<Columns>
							<Fields
								heading={t(this.state._headingName)}
								value={this.state._loanObject.other_info ? this.state._loanObject.other_info.name : ''}
							/>
							<Fields
								heading={t(this.state._headingEmail)}
								id="overflow"
								value={(() => {
									if (this.state._loanObject.other_info) {
										if (this.state._loanObject.other_info.emailId) {
											return this.state._loanObject.other_info.emailId;
										} else {
											return t('profile.none');
										}
									} else {
										return '';
									}
								})()}
							/>
							<Fields
								heading={t('view_detail.loan_amount')}
								value={`₱${this.state._loanObject.principal}`}
							/>
							<Fields heading={t('view_detail.lenders_state')} value={this.state.lenderState} />
							<Fields
								heading={t('label.first_payment_date')}
								value={GetFormat(this.state._loanObject.start_date)}
							/>
							<Fields
								heading={t('view_detail.bank_account')}
								value={
									!account.customer_id ||
									!account.funding_source_id ||
									account.plaid_status === 'ITEM_LOGIN_REQUIRED' ? (
										<Link
											href="#"
											onClick={e => {
												e.preventDefault();
												this.addBankAccount();
											}}
											style={{ fontSize: 'medium' }}
											variant="body2">
											{t('view_detail.add_account')}
										</Link>
									) : (
										`${account.institution_name} *${account.account_mask}`
									)
								}
							/>
							<Fields
								heading={t('view_detail.collateral')}
								value={`${this.state._loanObject.collateral || t('profile.none')}`}
							/>
						</Columns>
						<Columns>
							<Fields
								heading={t(this.state._headingPhone)}
								value={(() => {
									if (this.state._loanObject.other_info) {
										if (this.state._loanObject.other_info.phone) {
											return this.state._loanObject.other_info.phone;
										} else {
											return t('profile.none');
										}
									} else {
										return '';
									}
								})()}
							/>
							<Fields
								heading={t('view_detail.interest_rate')}
								value={this.state._loanObject.interest_rate + t('label.annually')}
							/>
							{this.state._loanObject.workflow_id === 2 ? (
								<Fields
									heading={t('view_detail.disbursal_date')}
									value={GetFormat(this.state._loanObject.disbursal_date)}
								/>
							) : null}
							<Fields
								heading={t('view_detail.number_of_installments')}
								value={this.state._loanObject.no_of_installments}
							/>
							<Fields
								heading={t('view_detail.frequency')}
								value={t(`label.${convertToLangKey(this.state._loanObject.frequency)}`)}
							/>
							<Fields
								heading={t('view_detail.loan_purpose')}
								value={
									this.state._loanObject.workflow_id === 3 ? (
										<Link
											href="#"
											onClick={e => {
												e.preventDefault();
												popUpForItemDescribed(this.state._loanObject.item_purchased);
											}}
											style={{ fontSize: 'medium' }}>
											{t('view_detail.pay_for_something')}
										</Link>
									) : (
										t(`label.${convertToLangKey(this.state._loanObject.reason)}`)
									)
								}
							/>
							<Fields
								heading={t('view_detail.additional_terms_conditions')}
								value={`${this.state._loanObject.additional_terms || t('profile.none')}`}
							/>
						</Columns>
						<ButtonColumns>
							<Buttons
								action={this.openAmortizationTable}
								id="all-button"
								name="buttons.view_amortization_table"
							/>
							<Buttons
								action={this.onAgreeAndContinue}
								disabled={this.state._isTableLoaded}
								id={this.state._isInviteSending ? 'disabled-button' : 'agree-button'}
								isLoading={this.state._isInviteSending}
								name="buttons.continue"
							/>
						</ButtonColumns>
						<ButtonColumns>
							<Buttons action={this.toastWhenCancelLoan} id="cancel-button" name="buttons.cancel_loan" />
							<Buttons action={this.onBack} id="all-button" name="buttons.back" />
						</ButtonColumns>
					</Base>
				);
		}
	}
}

export default hot(module)(withTranslation()(ViewSummary));
